import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBroadcastLists,
  createBroadcastList,
  updateBroadcastList,
  deleteBroadcastList,
  addContactsToList,
  removeContactFromList,
} from '../store/slices/broadcastListsSlice';
import { fetchCustomers } from '../store/slices/customersSlice';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './BroadcastLists.css';

// ─── Create / Edit List Modal ────────────────────────────────────────────────
const ListFormModal = ({ isOpen, onClose, onSave, editingList, customers, customersLoading }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [customerSearch, setCustomerSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingList) {
        setName(editingList.name || '');
        setDescription(editingList.description || '');
        const ids = (editingList.contacts || []).map(c => c._id || c.id || c);
        setSelectedIds(new Set(ids));
      } else {
        setName('');
        setDescription('');
        setSelectedIds(new Set());
      }
      setCustomerSearch('');
    }
  }, [isOpen, editingList]);

  const filteredCustomers = customers.filter(c => {
    const q = customerSearch.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  });

  const toggleCustomer = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredCustomers.map(c => c._id || c.id)));
  };

  const clearAll = () => setSelectedIds(new Set());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    const contactPayload = customers
      .filter(c => selectedIds.has(c._id || c.id))
      .map(c => ({
        name: c.name || '',
        phoneNumber: c.phoneNumber || c.whatsappNumber || '',
      }));
    await onSave({ name: name.trim(), description: description.trim(), contacts: contactPayload });
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="bl-modal-overlay" onClick={onClose}>
      <div className="bl-modal" onClick={e => e.stopPropagation()}>
        <div className="bl-modal-header">
          <h2>{editingList ? 'Edit broadcast list' : 'Create a broadcast list'}</h2>
          <button className="bl-modal-close" onClick={onClose} aria-label="Close dialog">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="bl-modal-body">
          <div className="bl-form-group">
            <label>List name <span className="required">*</span></label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., VIP customers"
              required
              className="bl-input"
            />
          </div>

          <div className="bl-form-group">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Add a short description (optional)"
              className="bl-input"
            />
          </div>

          <div className="bl-form-group">
            <div className="bl-picker-header">
              <label>
                Select contacts
                <span className="bl-badge">{selectedIds.size} selected</span>
              </label>
              <div className="bl-picker-actions">
                <button type="button" className="bl-link-btn" onClick={selectAll}>Select all shown</button>
                <span>·</span>
                <button type="button" className="bl-link-btn" onClick={clearAll}>Clear selection</button>
              </div>
            </div>

            <input
              type="text"
              value={customerSearch}
              onChange={e => setCustomerSearch(e.target.value)}
              placeholder="Search contacts by name, phone, or email"
              className="bl-input bl-input-sm"
            />

            <div className="bl-customer-list">
              {customersLoading ? (
                <div className="bl-customer-loading">Loading contacts…</div>
              ) : filteredCustomers.length === 0 ? (
                <div className="bl-customer-empty">
                  {customerSearch ? 'No contacts match your search.' : 'No contacts available.'}
                </div>
              ) : (
                filteredCustomers.map(c => {
                  const id = c._id || c.id;
                  const checked = selectedIds.has(id);
                  return (
                    <label key={id} className={`bl-customer-item${checked ? ' selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCustomer(id)}
                      />
                      <div className="bl-customer-avatar">
                        {(c.name || c.phone || '?')[0].toUpperCase()}
                      </div>
                      <div className="bl-customer-info">
                        <span className="bl-customer-name">{c.name || 'Unnamed'}</span>
                        <span className="bl-customer-phone">{c.phone || c.whatsappNumber || '—'}</span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="bl-modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : editingList ? 'Save list' : 'Create list'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── View Contacts Modal ──────────────────────────────────────────────────────
const ViewContactsModal = ({ isOpen, onClose, list, onRemoveContact }) => {
  const [removing, setRemoving] = useState(null);

  if (!isOpen || !list) return null;

  const handleRemove = async (contactId) => {
    setRemoving(contactId);
    await onRemoveContact(list._id || list.id, contactId);
    setRemoving(null);
  };

  const contacts = list.contacts || [];

  return (
    <div className="bl-modal-overlay" onClick={onClose}>
      <div className="bl-modal bl-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="bl-modal-header">
          <h2>
            {list.name}
            <span className="bl-badge bl-badge-neutral">
              {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
            </span>
          </h2>
          <button className="bl-modal-close" onClick={onClose} aria-label="Close dialog">✕</button>
        </div>
        <div className="bl-modal-body">
          {contacts.length === 0 ? (
            <div className="bl-empty-contacts">This broadcast list does not have any contacts yet.</div>
          ) : (
            <div className="bl-contact-list">
              {contacts.map(c => {
                const id = c._id || c.id || c;
                const name = c.name || 'Unnamed';
                const phone = c.phone || c.whatsappNumber || '—';
                return (
                  <div key={id} className="bl-contact-row">
                    <div className="bl-customer-avatar bl-avatar-sm">
                      {name[0].toUpperCase()}
                    </div>
                    <div className="bl-customer-info">
                      <span className="bl-customer-name">{name}</span>
                      <span className="bl-customer-phone">{phone}</span>
                    </div>
                    <button
                      className="bl-remove-btn"
                      onClick={() => handleRemove(id)}
                      disabled={removing === id}
                      title="Remove contact from list"
                      aria-label={`Remove ${name} from list`}
                    >
                      {removing === id ? '…' : '✕'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bl-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const BroadcastLists = () => {
  const dispatch = useDispatch();
  const { lists, loading, error } = useSelector(state => state.broadcastLists);
  const { customers, loading: customersLoading } = useSelector(state => state.customers);

  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);

  // Modals
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewingList, setViewingList] = useState(null);

  useEffect(() => {
    dispatch(fetchBroadcastLists());
    dispatch(fetchCustomers({ limit: 1000 }));
  }, [dispatch]);

  const showNotification = useCallback((msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  }, []);

  const handleOpenCreate = () => {
    setEditingList(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (list) => {
    setEditingList(list);
    setIsFormOpen(true);
  };

  const handleSave = async ({ name, description, contacts }) => {
    try {
      if (editingList) {
        await dispatch(updateBroadcastList({ id: editingList._id || editingList.id, data: { name, description } })).unwrap();
        // Sync contacts: add the full selection
        await dispatch(addContactsToList({ id: editingList._id || editingList.id, contacts })).unwrap();
        showNotification('Broadcast list updated successfully');
      } else {
        await dispatch(createBroadcastList({ name, description, contacts })).unwrap();
        showNotification('Broadcast list created successfully');
      }
      setIsFormOpen(false);
      setEditingList(null);
      dispatch(fetchBroadcastLists());
    } catch (err) {
      showNotification(err?.message || 'Something went wrong', 'error');
    }
  };

  const handleDeleteClick = (list) => {
    setListToDelete(list);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteBroadcastList(listToDelete._id || listToDelete.id)).unwrap();
      showNotification('Broadcast list deleted successfully');
      setIsDeleteOpen(false);
      setListToDelete(null);
    } catch (err) {
      showNotification(err?.message || 'Delete failed', 'error');
    }
  };

  const handleViewContacts = (list) => {
    setViewingList(list);
    setIsViewOpen(true);
  };

  const handleRemoveContact = async (listId, contactId) => {
    try {
      await dispatch(removeContactFromList({ listId, contactId })).unwrap();
      dispatch(fetchBroadcastLists());
      showNotification('Contact removed from broadcast list');
    } catch (err) {
      showNotification(err?.message || 'Failed to remove contact', 'error');
    }
  };

  const filtered = (lists || []).filter(l =>
    (l.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bl-container">
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success' ? '✓' : '✗'} {notification.msg}
        </div>
      )}

      {/* Header */}
      <div className="bl-header">
        <div className="header-content">
          <h1>Broadcast Lists</h1>
          <p>Organize contacts into groups for your WhatsApp broadcasts.</p>
        </div>
        <button className="btn-create-broadcast" onClick={handleOpenCreate}>
          + Create list
        </button>
      </div>

      {/* Toolbar */}
      <div className="bl-toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search broadcast lists…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <span className="bl-count-label">{filtered.length} list{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading broadcast lists…</p>
        </div>
      ) : error ? (
        <div className="bl-error-state">
          <p>⚠ {typeof error === 'string' ? error : 'Failed to load broadcast lists.'}</p>
          <button className="btn-primary" onClick={() => dispatch(fetchBroadcastLists())}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bl-empty-state">
          <div className="bl-empty-icon">📋</div>
          <h3>{searchQuery ? 'No broadcast lists match your search' : 'No broadcast lists yet'}</h3>
          <p>
            {searchQuery
              ? 'Try another search term.'
              : 'Create your first list to organize contacts for WhatsApp broadcasts.'}
          </p>
          {!searchQuery && (
            <button className="btn-create-broadcast" onClick={handleOpenCreate}>+ Create list</button>
          )}
        </div>
      ) : (
        <div className="bl-grid">
          {filtered.map(list => {
            const id = list._id || list.id;
            const contactCount = (list.contacts || []).length;
            const createdAt = list.createdAt ? new Date(list.createdAt).toLocaleDateString() : '—';
            return (
              <div key={id} className="bl-card">
                <div className="bl-card-header">
                  <div className="bl-card-icon">📋</div>
                  <div className="bl-card-actions">
                    <button
                      className="bl-icon-btn"
                      title="Edit list"
                      onClick={() => handleOpenEdit(list)}
                    >✏️</button>
                    <button
                      className="bl-icon-btn bl-icon-btn-danger"
                      title="Delete list"
                      onClick={() => handleDeleteClick(list)}
                    >🗑️</button>
                  </div>
                </div>

                <h3 className="bl-card-title">{list.name}</h3>
                {list.description && (
                  <p className="bl-card-desc">{list.description}</p>
                )}

                <div className="bl-card-meta">
                  <div className="bl-meta-item">
                    <span className="bl-meta-icon">👥</span>
                    <span>{contactCount} contact{contactCount !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="bl-meta-item">
                    <span className="bl-meta-icon">📅</span>
                    <span>{createdAt}</span>
                  </div>
                </div>

                {contactCount > 0 && (
                  <div className="bl-avatar-stack">
                    {(list.contacts || []).slice(0, 5).map((c, i) => {
                      const cName = c.name || c.phone || '?';
                      return (
                        <div key={i} className="bl-avatar-chip" title={cName}>
                          {cName[0].toUpperCase()}
                        </div>
                      );
                    })}
                    {contactCount > 5 && (
                      <div className="bl-avatar-chip bl-avatar-more">+{contactCount - 5}</div>
                    )}
                  </div>
                )}

                <button
                  className="bl-card-view-btn"
                  onClick={() => handleViewContacts(list)}
                >
                  View contacts →
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <ListFormModal
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingList(null); }}
        onSave={handleSave}
        editingList={editingList}
        customers={customers || []}
        customersLoading={customersLoading}
      />

      <ViewContactsModal
        isOpen={isViewOpen}
        onClose={() => { setIsViewOpen(false); setViewingList(null); }}
        list={viewingList}
        onRemoveContact={handleRemoveContact}
      />

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={() => { setIsDeleteOpen(false); setListToDelete(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete Broadcast List"
        message={`Are you sure you want to delete "${listToDelete?.name}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default BroadcastLists;
